import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import LayoutOutlet from "./LayoutOutlet";
import KnowledgeBase from "../pages/KnowledgeBase";
import KnowledgeId from "../pages/KnowledgeId";
import Main from "../pages/Main";
import AiAgent from "../pages/AiAgent";
import BuyerPersonaModal from "./BuyerPersonaModal";
import AiAgentName from "../pages/AiAgentName";
import Chat from "../pages/Chat";
import IntegrationMarketing from "../pages/IntegrationMarketing";
import IntegrationSocial from "../pages/IntegrationSocial";
import { socketService } from "./socketService";
import MarkdownComponent from "./MarkdownComponent";
import LinkedInCallBackUrl from "./LinkedInCallBackUrl";
import AccountSetting from "../pages/AccountSetting";
import Member from "../pages/Member";
import Managespace from "../pages/Managespace";
import Billing from "../pages/Billing";
import Mysetting from "../pages/Mysetting";
import Myworkspace from "../pages/Myworkspace";
import Login from "../pages/Login";
import Login2 from "../pages/Login2";
import SocialLogin from "../pages/SocialLogin";
import DisableDevTools from "./DisableDevTools";
import GoogleSearchConsoleCallback from "./GoogleSearchConsoleCallback";
import NotionCallBackUrl from "./NotionCallBackUrl";
import Workspace3 from "../pages/Workspace3";
import Script from "../pages/Script";
import Reporting from "../pages/Reporting";
import ScriptId from "../pages/ScriptId";
import MonthlyCalender from "../pages/MonthlyCalender";
import useCommonApi from "../hooks/common-apis";
import SlackCallBackUrl from "./SlackCallBackUrl";
import PersonLevelID from "../pages/PersonLevelID";
import CompanyLevelID from "../pages/CompanyLevelID";
import IntegrationSales from "../pages/IntegrationSales";
import { EmailOutreach } from "../pages/EmailOutreach";
import { EmailCampaigns } from "../pages/EmailCampaigns";
import SMTPSetupGuide from "../pages/SMTPSetupGuide";
import GmailSetupGuide from "../pages/GmailSetupGuide";
import OutlookSetupGuide from "../pages/OutlookSetupGuide";
import Signup from "../pages/Signup";
import Signup2 from "../pages/Signup2";
import PaymentSuccess from "../pages/PaymentSuccess";
import PaymentFailed from "../pages/PaymentFailed";
import Unauthorized from "../pages/Unauthorized";
import ApolloCallBackUrl from "./ApolloCallBackUrl";
import ZohoCallBackUrl from "./ZohoCallBackUrl";
import { checkSubscriptionStatus, forceLogout } from "../lib/utils";
import { checkAppVersion, forceLogoutDueToVersion } from "../lib/versionCheck";
import withExpirationCheck from "./withExpirationCheck";
import DynamicPage from "../pages/DynamicPage";
import Feedback from "../pages/Feedback";
import IntentTopic from "../pages/IntentTopic";

function Routing() {
  const location = useLocation();
  const { getKnowledgeBaseList } = useCommonApi();
  let pathname = location.pathname;
  const navigate = useNavigate();
  const [showBuyerModal, setShowBuyerModal] = useState(undefined);

  const WrappedDynamicPage = withExpirationCheck(DynamicPage);

  //Below useEffect is used to handle the initial setup and checks when the component mounts or pathname changes
  useEffect(() => {
    let pathnameArray = ["/", "/login-otp", "/signup", "/signup-otp"];
    let login_token = localStorage.getItem("login_token");

    // Check subscription status before allowing access to any route
    // Skip check for public routes (login, signup, payment pages)
    const publicRoutes = [
      "/",
      "/login-otp",
      "/signup",
      "/signup-otp",
      "/social/login",
      "/payment/success",
      "/payment/failed",
    ];

    // Check app version first if user is logged in
    if (login_token && !publicRoutes.includes(pathname)) {
      const versionValid = checkAppVersion();
      if (!versionValid) {
        // Force logout user if version mismatch detected
        forceLogoutDueToVersion(navigate);
        return;
      }
    }

    if (!publicRoutes.includes(pathname)) {
      const subscriptionCheck = checkSubscriptionStatus();
      if (!subscriptionCheck.isValid) {
        // Force logout user if subscription is inactive and expired
        forceLogout(navigate);
        return;
      }
    }

    if (!pathnameArray.includes(pathname) && !login_token) {
      navigate("/");
    }
    if (!socketService.isConnected()) {
      // Initialize socket if login data exists
      socketService.initialize();
    }

    // Check buyer_persona status in sessionStorage on pathname change
    const buyerPersonaData = sessionStorage.getItem("buyer_persona");
    if (
      (pathname == "/monthly-calendar" || pathname == "/ai-agent") &&
      buyerPersonaData
    ) {
      try {
        const parsedData = JSON.parse(buyerPersonaData);
        if (
          !parsedData ||
          Object.keys(parsedData || {}).length == 0 ||
          parsedData?.status == 1
        ) {
          setShowBuyerModal("dismissible");
        }
      } catch (error) {
        console.error("Error parsing buyer_persona data:", error);
      }
    }

    return () => {
      // Cleanup on app unmount
      socketService.disconnect();
    };
  }, [location?.pathname, navigate, pathname]);

  const handleCloseBuyerModal = () => {
    setShowBuyerModal(undefined);
  };
  return (
    <>
      {/* <BrowserRouter> */}
      <DisableDevTools />
      {/* Buyer Persona Modal */}
      <BuyerPersonaModal
        open={showBuyerModal}
        removeModal={handleCloseBuyerModal}
      />

      <Routes>
        <Route path="/" element={<LayoutOutlet />}>
          <Route path="/" element={<Login />} />
          <Route path="/login-otp" element={<Login2 />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signup-otp" element={<Signup2 />} />
          <Route path="/social/login" element={<SocialLogin />} />
          <Route path="/knowledge" element={<KnowledgeBase />} />
          <Route
            path="/knowledge/knowledgebase-details"
            element={<KnowledgeId />}
          />
          <Route path="/monthly-calendar" element={<MonthlyCalender />} />
          <Route path="/pixel-details" element={<ScriptId />} />
          <Route path="/ai-agent" element={<AiAgent />} />
          <Route path="/ai-employee/:id" element={<AiAgentName />} />
          <Route path="/chat" element={<Chat />} />
          <Route
            path="/integration-marketing"
            element={<IntegrationMarketing />}
          />
          <Route path="/integration-social" element={<IntegrationSocial />} />
          <Route path="/integration-sales" element={<IntegrationSales />} />
          <Route path="/script" element={<Script />} />
          <Route path="/person-level-id" element={<PersonLevelID />} />
          <Route path="/company-level-id" element={<CompanyLevelID />} />
          <Route path="/email-outreach" element={<EmailOutreach />} />
          <Route path="/signals" element={<IntentTopic />} />
          <Route
            path="/email-campaigns/:providerId"
            element={<EmailCampaigns />}
          />
          <Route path="/guides/smtp-setup" element={<SMTPSetupGuide />} />
          <Route path="/guides/gmail-setup" element={<GmailSetupGuide />} />
          <Route path="/guides/outlook-setup" element={<OutlookSetupGuide />} />
          <Route path="/user-activity" element={<Reporting />} />
          <Route
            path="/linkedin-authentication"
            element={<LinkedInCallBackUrl />}
          />
          <Route path="/notion/callback" element={<NotionCallBackUrl />} />
          <Route path="/slack/callback" element={<SlackCallBackUrl />} />
          <Route path="/apollo/callback" element={<ApolloCallBackUrl />} />
          <Route path="/zoho/callback" element={<ZohoCallBackUrl />} />
          <Route
            path="/google/search_console"
            element={<GoogleSearchConsoleCallback />}
          />
          {/* <Route path="/markdown-viewer" element={<MarkdownComponent />} /> */}
          <Route path="/account-settings" element={<AccountSetting />} />
          <Route path="/member" element={<Member />} />
          {/* manage-space */}
          <Route path="/manage-space" element={<Managespace />} />
          {/* Billing */}
          <Route path="/billing" element={<Billing />} />
          {/* Feedback */}
          <Route path="/feedback" element={<Feedback />} />
          {/* my-settings */}
          <Route path="/my-settings" element={<Mysetting />} />
          {/* my-workspace */}
          <Route path="/my-workspace" element={<Myworkspace />} />
          <Route path="/add-workspace" element={<Workspace3 />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
        </Route>
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failed" element={<PaymentFailed />} />
        <Route
          path="/workspace/:type/:type_id/:request_id"
          element={<WrappedDynamicPage />}
        />
      </Routes>
      {/* </BrowserRouter> */}
    </>
  );
}

export default Routing;
